import { H5 } from "@sellar-io/components";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import { useSellarUser } from "../../hooks";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { ProfilePicture } from "../profilePicture/ProfilePicture";

type Comment = {
  id: number;
  createdAt: Date;
  message: string;
};

type CommentCreatedBy = {
  businessUserId: number;
  userId: number;
  userName: string | null;
  businessId: number;
  businessName: string;
  userProfileImageUrl: string | null;
  jobTitle: string | null;
};

export const Comment = ({
  comment,
  createdBy,
  lineClamp,
}: {
  comment: Comment;
  createdBy: CommentCreatedBy;
  lineClamp?: number;
}): ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const isInbound = business?.id !== createdBy.businessId;

  return (
    <>
      <span
        className={twMerge(
          "flex text-xs",
          isInbound ? "flex-end ml-4" : "mr-4 ml-auto",
        )}
      >
        {dayjs().to(dayjs(comment.createdAt))}
      </span>
      <div
        className={twMerge(
          "rounded-b-3xl p-4 pt-2",
          isInbound
            ? "mr-12 rounded-tr-3xl bg-zinc-100"
            : "ml-12 rounded-tl-3xl bg-zinc-600 text-white",
        )}
      >
        <div className="flex items-center gap-2 py-2">
          <ProfilePicture
            logoUrl={createdBy.userProfileImageUrl}
            name={createdBy.userName ?? createdBy.businessName}
            size={"medium"}
          />
          <div className="flex flex-col">
            <H5
              className={twMerge(
                isInbound ? "text-gray-500" : "text-neutral-200",
              )}
            >
              {createdBy.userName}{" "}
              {user && createdBy.userId === user.id && "(You)"}
            </H5>
            {createdBy.jobTitle && (
              <span
                className={twMerge(
                  "text-sm font-medium",
                  isInbound ? "text-gray-500" : "text-neutral-200",
                )}
              >
                {createdBy.jobTitle}
              </span>
            )}
          </div>
        </div>

        <pre
          className={twMerge(
            "break-words whitespace-pre-wrap text-white italic select-text",
            isInbound ? "text-gray-500" : "text-neutral-200",
            lineClamp && lineClamp > 0 && `line-clamp-${lineClamp}`,
          )}
        >
          {comment.message}
        </pre>
      </div>
    </>
  );
};
